/**
 * Departments Page - Microsoft 365 Style
 * Manage departments and assign department heads
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  BuildingRegular,
  PersonRegular,
  EyeRegular,
} from '@fluentui/react-icons';
import { SearchableDropdown, SlidePanel } from '../../components/common';
import { TableCommandBar } from '../../components/layout';
import { ApiClient, newToast } from '../../configurations/index.config';
import { useAppContext } from '../../contexts/AppContainer';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  departmentCard: {
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },
  departmentCardSelected: {
    border: '2px solid #008272',
    backgroundColor: '#f0f9f8',
  },
  departmentIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#008272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '24px',
    marginBottom: '12px',
  },
  departmentName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  departmentHead: {
    fontSize: '13px',
    color: '#605e5c',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
});

interface Department {
  id: string;
  department_name: string;
  description?: string;
  head_of_department?: string;
  head_name?: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

export const DepartmentFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { profileData } = useAppContext() as any;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    headId: '',
  });

  useEffect(() => {
    if (profileData?.userId) {
      fetchDepartments();
      fetchEmployees();
    }
  }, [profileData?.userId]);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-departments');
      if (response.data.status === 'success') {
        setDepartments(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch departments');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        const employeeData = response.data.data || [];
        // Deduplicate employees by id
        const uniqueEmployees = employeeData.filter(
          (emp: Employee, index: number, self: Employee[]) =>
            index === self.findIndex(e => e.id === emp.id)
        );
        setEmployees(uniqueEmployees);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleOpenDialog = (dept?: Department) => {
    if (dept) {
      setSelectedDept(dept);
      setFormData({
        name: dept.department_name,
        description: dept.description || '',
        headId: dept.head_of_department || '',
      });
    } else {
      setSelectedDept(null);
      setFormData({ name: '', description: '', headId: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      newToast('warning', 'Please enter department name');
      return;
    }

    try {
      // Using documented API endpoint with correct field names
      const payload = {
        name: formData.name,
        description: formData.description,
        ...(selectedDept && { id: selectedDept.id }),
      };

      const response = await ApiClient.post('/add-update-department', payload);

      if (response.data.status === 'success') {
        newToast(
          'success',
          selectedDept ? 'Department updated successfully' : 'Department created successfully'
        );
        fetchDepartments();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save department');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleViewDetails = (dept: Department) => {
    navigate(`/account/department-details/${dept.id}`, { state: { department: dept } });
  };

  const handleDelete = async () => {
    if (!selectedDept) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-department', {
        id: selectedDept.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Department deleted successfully');
        fetchDepartments();
      } else {
        newToast('error', response.data?.message || 'Failed to delete department');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedDept(null);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Departments</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${departments.length} departments`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Department" />

      {/* Departments Grid */}
      {loading ? (
        <div className={styles.emptyState}>
          <Text>Loading departments...</Text>
        </div>
      ) : departments.length === 0 ? (
        <div className={styles.emptyState}>
          <BuildingRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
          <Text size={500} weight="semibold" style={{ display: 'block' }}>
            No departments found
          </Text>
          <Text style={{ marginTop: '8px', display: 'block' }}>
            Click "Add Department" to create your first department
          </Text>
        </div>
      ) : (
        <div className={styles.grid}>
          {departments.map(dept => (
            <div
              key={dept.id}
              className={styles.departmentCard}
              onClick={() => setSelectedDept(dept)}
            >
              <div className={styles.departmentIcon}>
                <BuildingRegular />
              </div>
              <Text className={styles.departmentName}>{dept.department_name}</Text>
              {dept.description && (
                <Text style={{ fontSize: '13px', color: '#605e5c', marginTop: '4px' }}>
                  {dept.description}
                </Text>
              )}
              {dept.head_name && (
                <div className={styles.departmentHead}>
                  <PersonRegular style={{ fontSize: '14px' }} />
                  <span>Head: {dept.head_name}</span>
                </div>
              )}
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<EyeRegular />}
                  onClick={e => {
                    e.stopPropagation();
                    handleViewDetails(dept);
                  }}
                >
                  View
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<EditRegular />}
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenDialog(dept);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedDept(dept);
                    setDeleteDialogOpen(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedDept ? 'Edit Department' : 'Add Department'}
        subtitle={selectedDept ? 'Update department details' : 'Create a new department'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedDept ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Label required>Department Name</Label>
          <Input
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter department name"
          />
        </div>

        <div className={styles.formField}>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description (optional)"
            rows={3}
          />
        </div>

        <div className={styles.formField}>
          <Label>Department Head</Label>
          <SearchableDropdown
            placeholder="Search employee..."
            options={[
              { id: '', label: 'None' },
              ...employees.map(emp => ({
                id: emp.id,
                label: `${emp.first_name} ${emp.last_name}`,
              })),
            ]}
            selectedValue={formData.headId}
            onSelect={value => setFormData({ ...formData, headId: value })}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Department</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedDept?.department_name}</strong>? This
              action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DepartmentFluentUI;
